import { createTheme } from './createTheme';
import { DARK_MODE, GRAY_SCALE } from 'constants/ThemeConstant';
import { rgba, createOpaqueTableRowColors, buildOpaqueTableRowScopedStyles, getTableRowTokens, getColoredHeaderTableTokens, buildThemeTableSorterScopedStyles, buildThemeCheckboxScopedStyles, buildThemeGroupRowCssVariables, buildThemePaginationScopedStyles, getThemePaginationTokens } from './utils';

const MID_AUTUMN_PRIMARY = '#FA8C16';
const MID_AUTUMN_BG = '#FFF7E6';
const MID_AUTUMN_TEXT = '#613400';
const MID_AUTUMN_BORDER = '#FFD591';
const MID_AUTUMN_GOLD = rgba('#FFD700', 0.9);
/** Icon module-menu (menu trái) — vàng đậm, đồng bộ pattern Tết / 20-10 */
const MID_AUTUMN_MODULE_ICON = '#E6B800';
const MID_AUTUMN_TABLE_ROW_COLORS = createOpaqueTableRowColors(MID_AUTUMN_PRIMARY, GRAY_SCALE.WHITE);
/** Sidebar custom-menu — không dùng token Menu global */
export const midAutumnCustomMenuStyles = `
.side-nav .custom-menu .ant-menu-item:not(.ant-menu-item-selected),
.side-nav .custom-menu .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title {
    color: ${MID_AUTUMN_TEXT} !important;
}
.side-nav .custom-menu .ant-menu-item:not(.ant-menu-item-selected) .anticon,
.side-nav .custom-menu .ant-menu-item:not(.ant-menu-item-selected) .ant-menu-item-icon,
.side-nav .custom-menu .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title .anticon,
.side-nav .custom-menu .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title .ant-menu-item-icon {
    color: ${MID_AUTUMN_GOLD} !important;
}
.side-nav .custom-menu .ant-menu-item-group-title {
    color: ${MID_AUTUMN_GOLD} !important;
}
.custom-menu-popup.ant-menu-dark .ant-menu-item:not(.ant-menu-item-selected),
.custom-menu-popup.ant-menu-dark .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open):not(.ant-menu-submenu-active) > .ant-menu-submenu-title {
    color: ${GRAY_SCALE.WHITE} !important;
}
.custom-menu-popup.ant-menu-dark .ant-menu-item:not(.ant-menu-item-selected) .anticon,
.custom-menu-popup.ant-menu-dark .ant-menu-submenu:not(.ant-menu-submenu-selected):not(.ant-menu-submenu-open) > .ant-menu-submenu-title .anticon {
    color: ${MID_AUTUMN_GOLD} !important;
}
`;

/** Steps điều hướng form (className="step-nav") */
export const midAutumnStepNavStyles = `
.step-nav.ant-steps .ant-steps-item-title {
    color: ${MID_AUTUMN_TEXT} !important;
    font-size: 13px !important;
}
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-title,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-title {
    color: ${MID_AUTUMN_PRIMARY} !important;
    font-weight: 600 !important;
}
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-icon,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-icon {
    background-color: transparent !important;
    border-color: ${MID_AUTUMN_PRIMARY} !important;
}
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-icon .ant-steps-icon,
.step-nav.ant-steps .ant-steps-item-process .ant-steps-item-icon .anticon,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-icon .ant-steps-icon,
.step-nav.ant-steps .ant-steps-item-finish .ant-steps-item-icon .anticon {
    color: ${MID_AUTUMN_PRIMARY} !important;
}
.step-nav.ant-steps .ant-steps-item-wait .ant-steps-item-icon {
    background-color: transparent !important;
    border-color: ${MID_AUTUMN_BORDER} !important;
}
.step-nav.ant-steps .ant-steps-item-wait .ant-steps-item-icon .ant-steps-icon,
.step-nav.ant-steps .ant-steps-item-wait .ant-steps-item-icon .anticon {
    color: ${GRAY_SCALE.GRAY} !important;
}
`;

export const midAutumnScopedStyles = `
${midAutumnCustomMenuStyles}
${midAutumnStepNavStyles}
${buildThemePaginationScopedStyles(MID_AUTUMN_GOLD, MID_AUTUMN_BORDER)}
.ant-table-wrapper .ant-table-thead > tr > th {
    font-weight: 600;
    text-transform: uppercase;
}
${buildThemeTableSorterScopedStyles(MID_AUTUMN_PRIMARY)}
${buildOpaqueTableRowScopedStyles(MID_AUTUMN_TABLE_ROW_COLORS)}
${buildThemeCheckboxScopedStyles('light')}
${buildThemeGroupRowCssVariables(MID_AUTUMN_PRIMARY, MID_AUTUMN_GOLD, 'light', MID_AUTUMN_BG)}
`;

export const midAutumnTheme = createTheme({
    primaryColor: MID_AUTUMN_PRIMARY,
    successColor: '#FFD666',
    warningColor: '#FFC53D',
    errorColor: '#FF4D4F',

    bgColor: MID_AUTUMN_BG,
    textColor: MID_AUTUMN_TEXT,
    borderColor: MID_AUTUMN_BORDER,
    bodyBg: MID_AUTUMN_BG,
    colorBgLayout: MID_AUTUMN_BG,

    menuGroupTitleColor: MID_AUTUMN_GOLD,
    moduleMenuBg: MID_AUTUMN_PRIMARY,
    moduleMenuIconColor: MID_AUTUMN_MODULE_ICON,
    moduleMenuItemColor: GRAY_SCALE.WHITE,
    moduleMenuItemSelectedBg: GRAY_SCALE.WHITE,
    moduleMenuItemSelectedColor: MID_AUTUMN_PRIMARY,
    headerNavColor: MID_AUTUMN_BG,
    siderBg: GRAY_SCALE.WHITE,
    customMenuBg: GRAY_SCALE.WHITE,
    customMenuItemSelectedColor: MID_AUTUMN_TEXT,
    customMenuItemSelectedBg: MID_AUTUMN_BG,
    customMenuItemSelectedIconColor: MID_AUTUMN_PRIMARY,
    customMenuItemSelectedBorderColor: MID_AUTUMN_GOLD,
    footerBg: MID_AUTUMN_PRIMARY,
    footerTextColor: GRAY_SCALE.WHITE,

    componentOverrides: {
        Layout: {
            bodyBg: MID_AUTUMN_BG,
            headerBg: MID_AUTUMN_BG,
            siderBg: GRAY_SCALE.WHITE,
        },
        Menu: {
            darkItemBg: 'transparent',
            darkItemColor: GRAY_SCALE.WHITE,
            darkItemSelectedBg: DARK_MODE.ACTIVE_BG_COLOR,
            darkItemSelectedColor: GRAY_SCALE.WHITE,
            darkItemHoverBg: DARK_MODE.HOVER_BG_COLOR,
            darkItemHoverColor: GRAY_SCALE.GRAY_DARK,
            darkSubMenuItemBg: DARK_MODE.BG_COLOR,
            popupBg: DARK_MODE.BG_COLOR,
            activeBarWidth: 0,
            boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
        },
        Breadcrumb: {
            itemColor: GRAY_SCALE.GRAY,
            lastItemColor: MID_AUTUMN_PRIMARY,
            linkColor: GRAY_SCALE.GRAY,
            linkHoverColor: MID_AUTUMN_PRIMARY,
            separatorColor: GRAY_SCALE.GRAY,
        },
        Card: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorTextHeading: MID_AUTUMN_PRIMARY,
            colorText: MID_AUTUMN_TEXT,
            colorBorderSecondary: MID_AUTUMN_BORDER,
            extraColor: MID_AUTUMN_TEXT,
            headerBg: GRAY_SCALE.WHITE,
            actionsBg: GRAY_SCALE.WHITE,
            borderRadiusLG: 22,
        },
        Table: {
            colorText: MID_AUTUMN_TEXT,
            colorTextHeading: GRAY_SCALE.WHITE,
            headerBg: MID_AUTUMN_PRIMARY,
            borderColor: '#F0F0F0',
            ...getColoredHeaderTableTokens(MID_AUTUMN_PRIMARY),
            ...getTableRowTokens(MID_AUTUMN_TABLE_ROW_COLORS),
        },
        Steps: {
            colorPrimary: MID_AUTUMN_PRIMARY,
            colorText: MID_AUTUMN_TEXT,
            colorTextDescription: GRAY_SCALE.GRAY,
        },
        Input: {
            colorBgContainer: GRAY_SCALE.WHITE,
            colorBorder: MID_AUTUMN_BORDER,
            activeBorderColor: MID_AUTUMN_PRIMARY,
            hoverBorderColor: MID_AUTUMN_PRIMARY,
        },
        Pagination: getThemePaginationTokens(MID_AUTUMN_PRIMARY, MID_AUTUMN_BORDER),
    },
});
